import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { catchError, forkJoin, from, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { escapeHtml } from '../../../shared/utils/escape-html';
import {
  BillingEntry,
  CallDetailsDialogData,
  CallDetailsTab,
  CallNoteDetails,
  CallNoteEntry,
  CdrAccountingEntry,
  TranscriptEntry,
} from './call-details.model';
import { CallDetailsService, isRecordingNotLocatable } from './call-details.service';
import { EmployeeApiService } from '../../../core/services/employee-api.service';
import { LOG_EVENTS } from '../../../core/services/log-events';
import { LoggingService, LogFields } from '../../../core/services/logging.service';
import { PermissionService } from '../../../core/auth/permission.service';
import { CallDetailsTabComponent } from './call-details-tab.component';
import { TranscriptFeedbackBarComponent } from './transcript-feedback/transcript-feedback-bar.component';
import { TranscriptFeedbackService } from './transcript-feedback/transcript-feedback.service';
import { callDirectionOf } from './transcript-feedback/call-direction';
import { CallNoteDetailsTabComponent } from './call-note-details-tab.component';
import {
  HLM_DIALOG_SHELL,
  HLM_MENU,
  HLM_TABS,
  HlmAlertComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmTooltipDirective,
  ToastService,
} from '../../../ui';

/** Berlin timestamp formatter — shared across timestamp-style columns. */
/** `MediaError.code` values — not read off the global, which the test env lacks. */
const MEDIA_ERR_ABORTED = 1;
const MEDIA_ERR_NETWORK = 2;
const MEDIA_ERR_DECODE = 3;

const BERLIN_FMT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatBerlin(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return BERLIN_FMT.format(d).replace(', ', ' ');
}

function berlinFormatter(p: ValueFormatterParams): string {
  return formatBerlin(p.value);
}

/**
 * Time-only formatter for the CDR Timestamp column: "HH:mm:ss". The date
 * lives in the separator row injected by `withRowDateSeparators` below.
 */
const BERLIN_TIME_FMT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function timeOnlyFormatter(p: ValueFormatterParams): string {
  const v = p.value as string | null | undefined;
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return BERLIN_TIME_FMT.format(d);
}

/** Date label for the separator banner: "Mo, 20.03.2026". */
const BERLIN_DATE_FMT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatDateBanner(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return BERLIN_DATE_FMT.format(d);
}

/** Marker type for synthetic date-separator rows injected into a table's rowData. */
interface RowDateSeparator {
  __sep: true;
  date: string;
}

/**
 * Generic stream operator that injects a RowDateSeparator before the first row
 * of each distinct Berlin-date. Rows are sorted ascending by the date accessor
 * first so separators sit above their group. Used by both the CDR and Call
 * Notes tabs — they pass different date accessors (`timestamp` vs `created`).
 */
function withDateSeparators<T>(
  rows: ReadonlyArray<T>,
  getDate: (row: T) => string | null | undefined,
): (T | RowDateSeparator)[] {
  if (!rows || rows.length === 0) return [...(rows ?? [])];
  const sorted = [...rows].sort((a, b) =>
    String(getDate(a) ?? '').localeCompare(String(getDate(b) ?? '')),
  );
  const out: (T | RowDateSeparator)[] = [];
  let lastDate: string | null = null;
  for (const r of sorted) {
    const iso = getDate(r) ?? '';
    if (!iso) {
      out.push(r);
      continue;
    }
    // Berlin local date — slice from a formatted dd.MM.yyyy stamp so DST quirks
    // don't accidentally move the separator across midnight.
    const dateKey = formatBerlin(iso).slice(0, 10);
    if (dateKey && dateKey !== lastDate) {
      out.push({ __sep: true, date: iso });
      lastDate = dateKey;
    }
    out.push(r);
  }
  return out;
}

const EUR_FMT = new Intl.NumberFormat('de-DE', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/** Currency formatter for unit price / sum — adds the € symbol. */
const EUR_CURRENCY_FMT = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

function moneyFormatter(p: ValueFormatterParams): string {
  if (p.value == null) return '';
  return EUR_FMT.format(Number(p.value));
}

function eurFormatter(p: ValueFormatterParams): string {
  if (p.value == null) return '';
  const n = Number(p.value);
  if (!Number.isFinite(n)) return '';
  return EUR_CURRENCY_FMT.format(n);
}

function amountFormatter(p: ValueFormatterParams): string {
  const v = p.value as number | null | undefined;
  if (v == null) return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '';
}

/**
 * Sum chip — bold + foreground tint when > 0, muted when 0. Operators scan the
 * Sum column for non-zero rows (the ones that actually cost money), so making
 * the zero rows visually retreat is the right contrast.
 */
function sumCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as number | null | undefined;
  if (v == null) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  const formatted = escapeHtml(EUR_CURRENCY_FMT.format(n));
  if (n === 0) return `<span class="text-muted-foreground">${formatted}</span>`;
  return `<span class="font-semibold text-foreground">${formatted}</span>`;
}

/**
 * Six-hue palette for hash-colored feature chips on the Billing tab. Each
 * feature code (sms_vers, cc_min, genGesMail, …) hashes to a fixed bucket,
 * so the same feature always carries the same tone across rows.
 * All `/20` opacity per SPARTAN §7.2 (Clean Teal contrast canary).
 */
const FEATURE_HUE_TONE: string[] = [
  'bg-primary/20 text-primary',
  'bg-success/20 text-success',
  'bg-warning/20 text-warning',
  'bg-accent/30 text-accent-foreground',
  'bg-ring-purple/20 text-ring-purple',
  'bg-destructive/20 text-destructive',
];

/**
 * Speaker tone palette — AGENT in primary (the ebuero side), CALLER in
 * ring-purple (the customer side; theme-stable hue). BOT / SYSTEM use muted.
 * Anything else falls through to a hash-colored chip via FEATURE_HUE_TONE.
 */
const SPEAKER_TONE: Record<string, string> = {
  AGENT:    'bg-primary/20 text-primary',
  EMPLOYEE: 'bg-primary/20 text-primary',
  EBUERO:   'bg-primary/20 text-primary',
  CALLER:   'bg-ring-purple/20 text-ring-purple',
  CUSTOMER: 'bg-ring-purple/20 text-ring-purple',
  USER:     'bg-ring-purple/20 text-ring-purple',
  BOT:      'bg-muted text-muted-foreground',
  SYSTEM:   'bg-muted text-muted-foreground',
};

function speakerCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as string | null | undefined;
  if (v == null || v === '') return '';
  const text = String(v);
  const tone = SPEAKER_TONE[text.toUpperCase()]
    ?? FEATURE_HUE_TONE[hueIndex(text.toUpperCase(), FEATURE_HUE_TONE.length)];
  return `<span class="inline-flex items-center rounded-full ${tone} px-2 py-0.5 text-xs font-semibold">${escapeHtml(text)}</span>`;
}

function featureCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as string | null | undefined;
  if (v == null || v === '') return '';
  const text = String(v);
  const tone = FEATURE_HUE_TONE[hueIndex(text.toUpperCase(), FEATURE_HUE_TONE.length)];
  return `<span class="inline-flex items-center rounded-full ${tone} px-2 py-0.5 text-xs font-semibold font-mono-feat" title="${escapeHtml(text)}">${escapeHtml(text)}</span>`;
}

/**
 * Attachments chip — paperclip icon + count, full file list in the title
 * tooltip. Empty cell for none. Accent tone keeps it from competing with
 * Status/Urgent chips.
 */
function attachmentsCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as { fileMetadata?: { name?: string } }[] | null | undefined;
  if (!v || v.length === 0) return '';
  const names = v
    .map((a) => a.fileMetadata?.name ?? '')
    .filter((n) => n.length > 0)
    .join('\n');
  const label = v.length === 1 ? '1 file' : `${v.length} files`;
  return (
    `<span class="inline-flex items-center gap-1 rounded-full bg-accent/30 px-2 py-0.5 text-xs font-semibold text-accent-foreground" title="${escapeHtml(names)}">` +
      `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
        `<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>` +
      `</svg>` +
      `${label}` +
    `</span>`
  );
}

/**
 * Human-readable size: 0 / null -> blank, < 1k bytes, < 1 MB kB, else MB.
 * Mono+right-aligned via the column cellClass.
 */
function sizeFormatter(p: ValueFormatterParams): string {
  const v = p.value as number | null | undefined;
  if (v == null || v === 0) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Tone palette for Call Notes Type — distinct chip color per medium so the
 * Type column reads at a glance (EMAIL is blue, SMS green, FAX amber, …).
 * Mirrors the calls-list TYPE_TONE pattern but for document/note types. All
 * background opacities are >= /20 per SPARTAN §7.2.
 */
const NOTE_TYPE_TONE: Record<string, string> = {
  EMAIL:     'bg-primary/20 text-primary',
  MAIL:      'bg-primary/20 text-primary',
  SMS:       'bg-success/20 text-success',
  FAX:       'bg-warning/20 text-warning',
  NOTE:      'bg-accent/30 text-accent-foreground',
  CALLNOTE:  'bg-accent/30 text-accent-foreground',
  VOICEMAIL: 'bg-ring-purple/20 text-ring-purple',
  CHAT:      'bg-success/20 text-success',
  TASK:      'bg-ring-purple/20 text-ring-purple',
};

function noteTypeCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as string | null | undefined;
  if (v == null || v === '') return '';
  const text = String(v);
  const tone = NOTE_TYPE_TONE[text.toUpperCase()] ?? 'bg-muted text-muted-foreground';
  return `<span class="inline-flex items-center rounded-full ${tone} px-2 py-0.5 text-xs font-semibold">${escapeHtml(text)}</span>`;
}

/**
 * Status tone for Call Notes — semantic palette per status code. SENT/RECEIVED
 * lean positive, DRAFT is in-progress, FAILED/REJECTED is destructive,
 * READ/ARCHIVED settle to muted. Unknown values fall back to muted.
 */
const NOTE_STATUS_TONE: Record<string, { pill: string; dot: string }> = {
  SENT:      { pill: 'bg-success/20 text-success',         dot: 'bg-success' },
  DELIVERED: { pill: 'bg-success/20 text-success',         dot: 'bg-success' },
  RECEIVED:  { pill: 'bg-primary/20 text-primary',         dot: 'bg-primary' },
  UNREAD:    { pill: 'bg-primary/20 text-primary',         dot: 'bg-primary' },
  READ:      { pill: 'bg-muted text-muted-foreground',     dot: 'bg-muted-foreground' },
  DRAFT:     { pill: 'bg-warning/20 text-warning',         dot: 'bg-warning' },
  PENDING:   { pill: 'bg-warning/20 text-warning',         dot: 'bg-warning' },
  FAILED:    { pill: 'bg-destructive/20 text-destructive', dot: 'bg-destructive' },
  REJECTED:  { pill: 'bg-destructive/20 text-destructive', dot: 'bg-destructive' },
  ARCHIVED:  { pill: 'bg-muted text-muted-foreground',     dot: 'bg-muted-foreground' },
};

function noteStatusCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as string | null | undefined;
  if (v == null || v === '') return '';
  const text = String(v);
  const tone = NOTE_STATUS_TONE[text.toUpperCase()]
    ?? { pill: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' };
  return (
    `<span class="inline-flex items-center gap-1.5 rounded-full ${tone.pill} px-2 py-0.5 text-xs font-semibold">` +
      `<span class="h-1.5 w-1.5 flex-none rounded-full ${tone.dot}"></span>` +
      `${escapeHtml(text)}` +
    `</span>`
  );
}

/**
 * Urgent — Yes destructive chip, No muted chip, empty when unset. The boolean
 * chip pattern matches what operators expect from the old Kundenverwaltung view
 * (a column you can scan visually for the rare 'true' values).
 */
function urgentCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as boolean | null | undefined;
  if (v === true) {
    return `<span class="inline-flex items-center rounded-full bg-destructive/20 px-2 py-0.5 text-xs font-semibold text-destructive">Yes</span>`;
  }
  if (v === false) {
    return `<span class="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">No</span>`;
  }
  return '';
}

/**
 * Priority — numeric 1-5. 1 = highest (destructive), 2 = warning, 3+ muted.
 * Mono digit + chip so the column reads as a status indicator at a glance.
 */
function priorityCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as number | null | undefined;
  if (v == null) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  let tone = 'bg-muted text-muted-foreground';
  if (n === 1) tone = 'bg-destructive/20 text-destructive';
  else if (n === 2) tone = 'bg-warning/20 text-warning';
  return `<span class="inline-flex items-center rounded-full ${tone} px-2 py-0.5 text-xs font-semibold font-mono-feat">${n}</span>`;
}

/**
 * Compact doc-ID renderer — short prefix (last 6 digits) + copy button. Used
 * for the `id` / `parentId` columns where IDs are distinct per row. Mono
 * font-feat so the digits line up vertically.
 */
function docIdCellRenderer(p: ValueFormatterParams): string {
  const v = p.value as number | string | null | undefined;
  if (v == null || v === '') return '';
  const full = escapeHtml(String(v));
  // Full ID — backend IDs are ~9-10 digits, well within a fixed-width column.
  // Inline-flex (left-aligned, no justify-between) keeps the ID + copy button
  // visually paired instead of stretching to fill the column width.
  return (
    `<span class="inline-flex items-center gap-1">` +
      `<span class="font-mono-feat" title="${full}">${full}</span>` +
      `<button type="button" ` +
        `class="cmt-copy-cchsid inline-flex h-4 w-4 flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground cursor-pointer" ` +
        `data-cchsid="${full}" ` +
        `aria-label="Copy ID" ` +
        `title="Copy ${full}">` +
        `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
          `<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>` +
          `<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>` +
        `</svg>` +
      `</button>` +
    `</span>`
  );
}

/**
 * Tone map for CDR action phases so the column reads as a colored timeline
 * (CALL → RING → CONNECT → END_CALL). Each phase gets a different chip color
 * matching its meaning. Unknown actions fall back to muted.
 */
const ACTION_TONE: Record<string, string> = {
  CDR_CALL:       'bg-primary/20 text-primary',           // call started — primary blue/teal
  CDR_RING:       'bg-ring-purple/20 text-ring-purple',   // ringing — purple
  CDR_FIRSTRING_OUT: 'bg-ring-purple/20 text-ring-purple',
  CDR_FIRSTRING_IN:  'bg-ring-purple/20 text-ring-purple',
  CDR_CONNECT:    'bg-success/20 text-success',           // connected — green
  CDR_CONNECT_OUT:'bg-success/20 text-success',
  CDR_END_CALL:   'bg-muted text-muted-foreground',       // ended — neutral grey
};

function actionCellRenderer(params: ValueFormatterParams): string {
  const value = params.value as string | null | undefined;
  if (value == null || value === '') return '';
  const text = String(value);
  const tone = ACTION_TONE[text.toUpperCase()] ?? 'bg-muted text-muted-foreground';
  return `<span class="inline-flex items-center rounded-full ${tone} px-2 py-0.5 text-xs font-semibold">${escapeHtml(text.replace(/^CDR_/, ''))}</span>`;
}

/**
 * Direction cell renderer — IN/OUT as a colored chip with a directional arrow.
 *   IN  → success green + ← arrow ("incoming" reads from right to left)
 *   OUT → primary blue + → arrow
 */
/**
 * Type cell renderer — muted neutral chip. Action carries the phase color
 * coding; Type is metadata about the connection (e.g. EXTERN, INTERN) so a
 * single soft tone keeps it from competing visually.
 */
function typeCellRenderer(params: ValueFormatterParams): string {
  const v = params.value as string | null | undefined;
  if (v == null || v === '') return '';
  return `<span class="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">${escapeHtml(String(v))}</span>`;
}

/**
 * Error code chip — destructive-tinted pill so error rows stand out.
 * Empty / null renders as em-dash (no chip noise on success rows).
 */
function errorCodeCellRenderer(params: ValueFormatterParams): string {
  const v = params.value as string | null | undefined;
  if (v == null || v === '') return '';
  return `<span class="inline-flex items-center rounded-full bg-destructive/20 px-2 py-0.5 text-xs font-semibold text-destructive font-mono-feat">${escapeHtml(String(v))}</span>`;
}

/**
 * Error reason chip — same destructive tint as the Code chip; truncates the
 * reason text with ellipsis inside the chip and exposes the full string via
 * a native title tooltip so hovering reveals everything.
 */
function errorReasonCellRenderer(params: ValueFormatterParams): string {
  const v = params.value as string | null | undefined;
  if (v == null || v === '') return '';
  const full = escapeHtml(String(v));
  return (
    `<span class="inline-flex max-w-full items-center rounded-full bg-destructive/20 px-2 py-0.5 text-xs font-medium text-destructive" title="${full}">` +
      `<span class="truncate">${full}</span>` +
    `</span>`
  );
}

function directionCellRenderer(params: ValueFormatterParams): string {
  const v = String(params.value ?? '').toUpperCase();
  if (v === 'IN') {
    // Success green is theme-stable; combined with the ← arrow it reads as "incoming".
    return (
      `<span class="inline-flex items-center gap-1 rounded-full bg-success/20 px-2 py-0.5 text-xs font-semibold text-success">` +
        `<span aria-hidden="true">←</span>IN` +
      `</span>`
    );
  }
  if (v === 'OUT') {
    // ring-purple is a fixed (theme-agnostic) hue declared in styles.tailwind.css.
    // We pick it instead of --primary because --primary is teal in Clean Teal
    // and clashes with --success (both greenish) — making IN and OUT indistinguishable.
    return (
      `<span class="inline-flex items-center gap-1 rounded-full bg-ring-purple/20 px-2 py-0.5 text-xs font-semibold text-ring-purple">` +
        `OUT<span aria-hidden="true">→</span>` +
      `</span>`
    );
  }
  return '';
}

/**
 * Hash an arbitrary string to a small palette index. Same Call ID always
 * maps to the same color across rows — operators can see which events
 * share a call-id at a glance.
 */
function hueIndex(id: string, buckets: number): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  return Math.abs(h) % buckets;
}

/**
 * Call-ID cell renderer — short prefix + ellipsis + copy button. The chip is
 * tinted by a hash of the full ID so rows that share a call-id pop visually.
 * The full ID stays available via the native title tooltip + the copy button.
 */
function callIdCellRenderer(params: ValueFormatterParams): string {
  const v = params.value as string | null | undefined;
  if (v == null || v === '') return '';
  // Keep the raw value for hash-index input (deterministic regardless of escape)
  // and the escaped version for every HTML interpolation. Skipping escape here
  // would let a malformed backend Call-ID with `"` or `<` break out of the chip.
  const raw = String(v);
  const safe = escapeHtml(raw);
  const hue = hueIndex(raw, 6);
  // First segment of UUID-style IDs reads as enough to distinguish; full value in title.
  const short = escapeHtml(raw.length > 10 ? raw.slice(0, 8) + '…' : raw);
  return (
    `<span class="cmt-callid-chip cmt-callid-hue-${hue}" title="${safe}">` +
      `<span class="font-mono-feat">${short}</span>` +
      `<button type="button" ` +
      `class="cmt-copy-cchsid inline-flex h-4 w-4 flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground cursor-pointer" ` +
      `data-cchsid="${safe}" ` +
      `aria-label="Copy Call ID to clipboard" ` +
      `title="Copy ${safe}">` +
      `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
        `<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>` +
        `<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>` +
      `</svg>` +
      `</button>` +
    `</span>`
  );
}

/**
 * Strip `sip:` prefix and `@host` suffix from a SIP URI for compactness.
 * "sip:+4915906196591@192.168.1.10" -> "+4915906196591"
 * Falls back to the original value if it isn't a SIP URI.
 */
function sipShortFormatter(params: ValueFormatterParams): string {
  const v = params.value as string | null | undefined;
  if (v == null || v === '') return '';
  const s = String(v);
  const m = s.match(/^sip:([^@]+)@/);
  return m ? m[1] : s;
}

/**
 * From / To cellRenderer — same shortening as sipShortFormatter plus an inline
 * copy button that reuses the global .cmt-copy-cchsid clipboard delegate. The
 * data-cchsid attribute holds the FULL original SIP URI (not the shortened
 * display value) so operators can paste it into Graylog / SIP traces.
 */
/**
 * Two-line From/To renderer for Call Notes — parses the typical mail-style
 * value `"Name" <user@host>` and renders Name on the top line and the email
 * (mono) on a second muted line. Falls back to a single-line render for
 * values that don't match (bare phone numbers, SIP URIs, plain strings).
 * Inline copy still targets the FULL original value.
 *
 * Sized to fit AG-Grid's default 42px row height: two `text-sm` lines with
 * `leading-tight` ≈ 34px content + cell padding.
 */
function fromToTwoLineCellRenderer(params: ValueFormatterParams): string {
  const raw = params.value as string | null | undefined;
  if (raw == null || raw === '') return '';
  const full = escapeHtml(String(raw));
  const s = String(raw).trim();
  // Match  "Name" <email>  or  Name <email>  — both common in mail headers.
  const m = s.match(/^"?([^"<]+?)"?\s*<\s*([^>\s]+)\s*>\s*$/);
  if (m) {
    const name = escapeHtml(m[1].trim());
    const email = escapeHtml(m[2].trim());
    return (
      `<span class="flex w-full items-center gap-1.5">` +
        `<span class="flex min-w-0 flex-1 flex-col leading-tight" title="${full}">` +
          `<span class="truncate text-foreground">${name}</span>` +
          `<span class="truncate font-mono-feat text-sm text-muted-foreground">${email}</span>` +
        `</span>` +
        `<button type="button" ` +
          `class="cmt-copy-cchsid inline-flex h-4 w-4 flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground cursor-pointer" ` +
          `data-cchsid="${full}" ` +
          `aria-label="Copy to clipboard" ` +
          `title="Copy ${full}">` +
          `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
            `<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>` +
            `<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>` +
          `</svg>` +
        `</button>` +
      `</span>`
    );
  }
  // Fallback — bare phone / SIP / plain string. Single-line, same shortening
  // as the CDR renderer (strips sip:…@host prefix when present).
  return fromToCellRenderer(params);
}

function fromToCellRenderer(params: ValueFormatterParams): string {
  const raw = params.value as string | null | undefined;
  if (raw == null || raw === '') return '';
  const full = escapeHtml(String(raw));
  const m = String(raw).match(/^sip:([^@]+)@/);
  const short = escapeHtml(m ? m[1] : String(raw));
  return (
    `<span class="inline-flex w-full items-center justify-between gap-1.5">` +
      `<span class="truncate" title="${full}">${short}</span>` +
      `<button type="button" ` +
        `class="cmt-copy-cchsid inline-flex h-4 w-4 flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground cursor-pointer" ` +
        `data-cchsid="${full}" ` +
        `aria-label="Copy to clipboard" ` +
        `title="Copy ${full}">` +
        `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
          `<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>` +
          `<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>` +
        `</svg>` +
      `</button>` +
    `</span>`
  );
}

// escapeHtml moved to shared/utils/escape-html.ts (imported at top of file).

/** Column defs for Accounting / CDR Data — CCHSID dropped (already in dialog header).
 *  Timestamp shows TIME only; the date sits on the separator row injected by
 *  `withRowDateSeparators` so identical dates aren't repeated on every row. */
const ACCOUNTING_COLS: ColDef<CdrAccountingEntry>[] = [
  { field: 'timestamp', headerName: 'Time', minWidth: 110, valueFormatter: timeOnlyFormatter, cellClass: 'font-mono-feat' },
  { field: 'action', headerName: 'Action', minWidth: 130, cellRenderer: actionCellRenderer },
  { field: 'direction', headerName: 'Dir.', minWidth: 70, maxWidth: 100, cellRenderer: directionCellRenderer, headerTooltip: 'Direction (IN / OUT)' },
  { field: 'from', headerName: 'From', minWidth: 180, valueFormatter: sipShortFormatter, tooltipField: 'from', cellClass: 'font-mono-feat' },
  { field: 'to', headerName: 'To', minWidth: 180, valueFormatter: sipShortFormatter, tooltipField: 'to', cellClass: 'font-mono-feat' },
  { field: 'callId1', headerName: 'Call ID 1', minWidth: 140, cellRenderer: callIdCellRenderer },
  { field: 'callid2', headerName: 'Call ID 2', minWidth: 140, cellRenderer: callIdCellRenderer },
  { field: 'type', headerName: 'Type', minWidth: 100 },
  { field: 'errorCode', headerName: 'Error Code', minWidth: 110 },
  { field: 'errorReason', headerName: 'Error Reason', minWidth: 150 },
  { field: 'agent', headerName: 'Agent', minWidth: 130 },
];

// Call Notes column defs live in a class getter (see `callNotesCols` on the
// component) so cellRenderers can close over component state for the future
// letter-chip palette and async lookups. The plain const is kept off-module.

// Billing column defs are built in a computed signal on the component (see
// `billingColumnDefs`) so the cellRenderers can close over component state
// (matches the Call Notes / CDR pattern).

// Transcript columns are built in a computed signal on the component (see
// `transcriptColumnDefs`) so we can reuse the speaker chip renderer + close
// over component state.
//
// Mock transcript data for non-live envs lives in `mock-transcript.dev.ts`
// and is loaded via dynamic `import()` from `fetchTranscript`. This keeps the
// sample German dialogue out of the live production bundle.

/** Time-only formatter (HH:mm:ss) reused for chat-bubble line timestamps. */
const CHAT_TIME_FMT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

@Component({
  selector: 'cmt-call-details-dialog',
  standalone: true,
  imports: [
    CallDetailsTabComponent,
    CallNoteDetailsTabComponent,
    HlmAlertComponent,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmTooltipDirective,
    TranscriptFeedbackBarComponent,
    ...HLM_DIALOG_SHELL,
    ...HLM_MENU,
    ...HLM_TABS,
  ],
  templateUrl: './call-details-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallDetailsDialogComponent {
  private readonly service = inject(CallDetailsService);
  private readonly employees = inject(EmployeeApiService);
  private readonly permissionService = inject(PermissionService);
  private readonly loggingService = inject(LoggingService);
  private readonly dialogRef = inject(MatDialogRef<CallDetailsDialogComponent>);
  // DestroyRef pipes every subscribe() through `takeUntilDestroyed()` so that
  // closing the dialog mid-flight cancels pending HTTP work + prevents `.set()`
  // calls on disposed signals (CLAUDE.md pitfall #11).
  private readonly destroyRef = inject(DestroyRef);
  readonly data: CallDetailsDialogData = inject(MAT_DIALOG_DATA);

  readonly activeTab = signal<CallDetailsTab>(this.data.initialTab ?? 'details');

  // EB-3805: Routing Manager Call Explainer deep-link.
  // Gated on the `routingmanager.read` module. Button stays visible without the
  // permission (so users can discover the feature) but is disabled with a tooltip.
  readonly canOpenCallExplainer = computed<boolean>(() =>
    this.permissionService.hasModule('routingmanager.read'),
  );
  readonly callExplainerUrl = computed<string>(
    () => `${environment.routingManagerUrl}/rm/research/call/${encodeURIComponent(this.data.cchsid)}`,
  );
  readonly callExplainerTooltip = computed<string>(() =>
    this.canOpenCallExplainer()
      ? 'Open this call in the Routing Manager Call Explainer'
      : 'Required permission: Routing Manager',
  );

  /** Suppress navigation when the user lacks the permission. The anchor still
   *  takes pointer events so hover-tooltip + cursor feedback work. */
  onOpenCallExplainerClick(event: Event): void {
    if (!this.canOpenCallExplainer()) {
      event.preventDefault();
      return;
    }
    /** @ticket EB-3798 — usage: jump-out to the Routing Manager Call Explainer (side entrance). */
    this.loggingService.usage('side_entrance.click', { target: 'routing_manager', section: 'calls' });
  }

  // EB-3807: "Open Graylog" share action — deep-links into Graylog, searching
  // the full-text index for this call's CCHSID over the last 30 days.
  //
  // TODO(EB-3807): permission gate piggybacks on the `isInDepartment('Technik')`
  // department sniff (originally added in EB-3795; that live-env gate was removed
  // in EB-4099, but this action is now the sole consumer keeping the sniff alive).
  // Replace with a proper permission module (e.g. `graylog.read` via `hasModule(...)`)
  // once the new auth API / a dedicated module lands. See TODO.md 2026-05-26.
  readonly canOpenGraylog = computed<boolean>(() =>
    this.permissionService.isInDepartment('Technik'),
  );

  /** Graylog relative-time search (30d) for the call's CCHSID, scoped to the
   *  configured stream so the query doesn't scan every index (avoids overloading
   *  Graylog). Full-text query keeps it field-name-agnostic across log sources.
   *  `environment.graylogUrl` already points at the `/search` endpoint, so we
   *  only append the query string. */
  readonly graylogUrl = computed<string>(() => {
    const query = `"${this.data.cchsid}"`;
    return `${environment.graylogUrl}?q=${encodeURIComponent(query)}`
      + `&rangetype=relative&from=2592000`
      + `&streams=${encodeURIComponent(environment.graylogStreamId)}`;
  });

  onOpenGraylog(): void {
    if (!this.canOpenGraylog()) return;
    /** @ticket EB-3798 — usage: jump-out to Graylog (side entrance). */
    this.loggingService.usage('side_entrance.click', { target: 'graylog', section: 'calls' });
    window.open(this.graylogUrl(), '_blank', 'noopener');
  }

  /** Whether the dialog spans the full viewport (no margins, no rounded corners). */
  readonly fullscreen = signal(false);

  /**
   * Surface size class — flips between the comfortable bordered modal and an edge-to-edge
   * fullscreen view. We use `fixed inset-0` for fullscreen because the CDK overlay container
   * caps the dialog at MatDialog's maxWidth/maxHeight; without `fixed`, w-screen overflows
   * the constrained container instead of becoming a true fullscreen layer. The height must
   * equal the `maxHeight: '92vh'` that calls.component opens the dialog with: a taller
   * surface is clipped by the dialog frame, which hid the bottom of every tab. The width
   * follows the same rule with its `width: '95vw'`: a wider surface overflows the frame on
   * screens narrower than the 1600px cap.
   */
  readonly surfaceClass = computed<string>(() =>
    this.fullscreen()
      ? 'fixed inset-0 z-50 w-screen h-screen max-w-none max-h-none rounded-none border-0'
      : 'w-[min(95vw,1600px)] max-w-[1600px] h-[92vh] max-h-[92vh]',
  );

  toggleFullscreen(): void {
    this.fullscreen.update((v) => !v);
  }

  // ---- Share -------------------------------------------------------------
  // Same Copy / Email / Teams trio as the calls page, but the URL also encodes the
  // active call's `cchsid` and the open `tab` so the recipient lands directly on this
  // exact dialog state.

  private readonly toast = inject(ToastService);

  /** Build the share URL: current location + cchsid + tab as queryParams. */
  private currentDialogShareUrl(): string {
    if (typeof window === 'undefined') return '';
    const url = new URL(window.location.href);
    url.searchParams.set('cchsid', this.data.cchsid);
    url.searchParams.set('tab', this.activeTab());
    return url.toString();
  }

  /** Short message text used as Email subject / Teams preview. */
  private dialogShareMessageText(): string {
    return `Call details · ${this.data.cchsid} (${this.activeTab()})`;
  }

  /** Copy the call's `cchsid` to the clipboard — used for support tickets / log correlation. */
  copyCchsid(): void {
    if (!this.data.cchsid) return;
    navigator.clipboard.writeText(this.data.cchsid).then(
      () => this.toast.open('Call ID copied', '', { duration: 1500 }),
      () => this.toast.open('Could not copy call ID', '', { duration: 2000, tone: 'error' }),
    );
  }

  /**
   * Copy the direct deep link to this call (same URL the Teams / Email share uses). Toasts are
   * copied verbatim from the Call Viewer page's share action (`CallsComponent.onShareCopy`).
   * @ticket EB-4153
   */
  copyLink(): void {
    const url = this.currentDialogShareUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => this.toast.open('Link copied to clipboard', '', { duration: 1800 }),
      () => this.toast.open('Could not copy link', 'Check browser permissions', { duration: 2500, tone: 'error' }),
    );
  }

  onShareEmail(): void {
    const url = this.currentDialogShareUrl();
    if (!url) return;
    const subject = this.dialogShareMessageText();
    const body = `${subject}\n\n${url}`;
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  }

  onShareTeams(): void {
    const url = this.currentDialogShareUrl();
    if (!url) return;
    const msg = this.dialogShareMessageText();
    // HTTPS variant — `msteams:` opens the app but drops the payload, see calls.component.
    const teamsUrl = `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&msgText=${encodeURIComponent(msg)}`;
    /** @ticket EB-3798 — usage: share the call details to Microsoft Teams (side entrance). */
    this.loggingService.usage('side_entrance.click', { target: 'teams', section: 'calls' });
    window.open(teamsUrl, '_blank', 'noopener');
  }

  // CDR columns: rebuild here (not as a module-level const) because the Call ID
  // renderer needs to close over `this.callIdLegendMap` for the letter / hue
  // lookup. Widened to include the synthetic RowDateSeparator union so the
  // generic tab's ColDef<T> input accepts the type.
  //
  // NOTE: built lazily inside ngOnInit via accountingCols getter once
  // callIdShortRenderer is initialised — TS class-field init order would
  // otherwise complain ('Property used before its initialization').
  private _accountingCols: ColDef<CdrAccountingEntry | RowDateSeparator>[] | null = null;
  get accountingCols(): ColDef<CdrAccountingEntry | RowDateSeparator>[] {
    if (this._accountingCols == null) {
      // Call IDs: two narrow single-header columns. We tried a two-tier 'Call ID'
      // parent group but it doubled the header row height (AG-Grid stretches all
      // sibling single-row headers to match the tallest one). The legend strip
      // above the table already makes the relationship clear.
      // Widths tuned for content + header alignment:
      // - Call ID 1/2: fit the 9-char header tightly (chip is 1.4rem letter only)
      // - Dir.: fit 'OUT →' chip (~55px) + a couple of pixels for the arrow
      // - Error Code / Error Reason: real codes are short (SIP 486 / numeric).
      //   Smaller defaults free width for Agent which carries name + ID.
      // - Type: now a muted chip (same family as Action) so the column reads
      //   as a status, not as raw data.
      // Order: identity (Time, Action, Dir) -> participants (From, To,
      // Call IDs) -> outcome (Ended, Agent) -> errors at the tail. Errors
      // are usually empty, so pushing them last keeps the warm part of the
      // table compact and signals 'admin/diagnostic info here'.
      this._accountingCols = [
        { field: 'timestamp', headerName: 'Time', minWidth: 110, maxWidth: 130, valueFormatter: timeOnlyFormatter, cellClass: 'font-mono-feat' },
        { field: 'action', headerName: 'Action', minWidth: 130, maxWidth: 170, cellRenderer: actionCellRenderer },
        { field: 'direction', headerName: 'Dir.', minWidth: 90, maxWidth: 110, cellRenderer: directionCellRenderer, headerTooltip: 'Direction (IN / OUT)' },
        { field: 'from', headerName: 'From', minWidth: 170, cellRenderer: fromToCellRenderer, cellClass: 'font-mono-feat' },
        { field: 'to', headerName: 'To', minWidth: 170, cellRenderer: fromToCellRenderer, cellClass: 'font-mono-feat' },
        { field: 'callId1', headerName: 'Call ID 1', minWidth: 95, maxWidth: 110, cellRenderer: this.callIdShortRenderer, headerTooltip: 'Call ID 1 — full value in the legend above the table' },
        { field: 'callid2', headerName: 'Call ID 2', minWidth: 95, maxWidth: 110, cellRenderer: this.callIdShortRenderer, headerTooltip: 'Call ID 2 — full value in the legend above the table' },
        // 'Ended' mirrors the old Kundenverwaltung 'Beendet' column — who ended the call (EXTERN, INTERN, …).
        { field: 'type', headerName: 'Ended', minWidth: 95, maxWidth: 130, cellRenderer: typeCellRenderer, headerTooltip: 'Who / how the call ended (old CMT: Beendet)' },
        { field: 'agent', headerName: 'Agent', minWidth: 220, cellRenderer: this.agentCellRenderer, headerTooltip: 'Agent (resolved name + ID)' },
        // Diagnostic columns last — only populated on error rows, both styled as
        // destructive-tinted chips so error rows visually pop without needing a
        // dedicated row-level highlight.
        { field: 'errorCode', headerName: 'Code', minWidth: 80, maxWidth: 110, headerTooltip: 'Error code (SIP / internal)', cellRenderer: errorCodeCellRenderer },
        { field: 'errorReason', headerName: 'Reason', minWidth: 140, maxWidth: 240, cellRenderer: errorReasonCellRenderer, headerTooltip: 'Hover the reason chip for the full error message' },
      ] as ColDef<CdrAccountingEntry | RowDateSeparator>[];
    }
    return this._accountingCols;
  }

  /**
   * Call Notes column defs — computed so it reacts to `subjectLegend()`. The
   * Subject column flips between hidden (single subject — shown in the summary
   * card instead), and a letter-chip palette when multiple distinct subjects
   * exist (same letter+hue scheme as the CDR Call-ID column). Diagnostic /
   * sparse columns sit at the tail; the warm part of the grid (when / what /
   * who) stays compact and scannable. ParentId is dropped — it's always the
   * dialog's parent call note, shown once in the summary card.
   */
  readonly callNotesColumnDefs = computed<ColDef<CallNoteEntry | RowDateSeparator>[]>(() => {
    const multiSubject = this.subjectLegend().length > 1;
    return [
      // Widths tuned to content (HH:mm:ss → ~80px; chips → just enough for the
      // widest value; Doc-ID short form → ~110px including the copy button).
      // sizeColumnsToFit distributes the remainder to the flex-eligible From /
      // To columns, which absorb most of the visual real estate.
      { field: 'created',     headerName: 'Time',   width: 80,  maxWidth: 95,  valueFormatter: timeOnlyFormatter, cellClass: 'font-mono-feat' },
      { field: 'type',        headerName: 'Type',   width: 90,  maxWidth: 110, cellRenderer: noteTypeCellRenderer, headerTooltip: 'Document type (EMAIL, SMS, FAX, NOTE, …)' },
      { field: 'status',      headerName: 'Status', width: 105, maxWidth: 130, cellRenderer: noteStatusCellRenderer },
      // Subject only appears when there's >1 distinct subject; renders as a
      // letter chip whose colour is shared with the legend strip above. With a
      // single subject the column disappears entirely and the subject lives in
      // the summary card.
      ...(multiSubject
        ? [{ field: 'subject', headerName: 'Subj.', width: 55, maxWidth: 70, cellRenderer: this.subjectShortRenderer, headerTooltip: 'Subject — full text in the legend above the table' } as ColDef<CallNoteEntry | RowDateSeparator>]
        : []),
      // Two-line render: name on top, email on bottom (mono, muted). Falls back to
      // single-line for bare numbers / SIP URIs.
      { field: 'from',        headerName: 'From',   width: 240, cellRenderer: fromToTwoLineCellRenderer, tooltipField: 'from' },
      { field: 'to',          headerName: 'To',     width: 240, cellRenderer: fromToTwoLineCellRenderer, tooltipField: 'to' },
      { field: 'priority',    headerName: 'Prio',   width: 55,  maxWidth: 70,  cellRenderer: priorityCellRenderer, headerTooltip: 'Priority (1 = highest)' },
      { field: 'urgent',      headerName: 'Urg.',   width: 60,  maxWidth: 75,  cellRenderer: urgentCellRenderer, headerTooltip: 'Urgent' },
      { field: 'attachments', headerName: 'Files',  width: 75,  maxWidth: 90,  cellRenderer: attachmentsCellRenderer, sortable: false, headerTooltip: 'Attachments — hover the chip for file names' },
      { field: 'size',        headerName: 'Size',   width: 65,  maxWidth: 80,  valueFormatter: sizeFormatter, cellClass: 'font-mono-feat text-right' },
      // Doc-ID: 9-10 digit full ID + copy button ≈ 115px. Tight max keeps
      // sizeColumnsToFit from inflating the column past its content.
      { field: 'id',          headerName: 'Doc-ID', width: 120, maxWidth: 135, cellRenderer: docIdCellRenderer, headerTooltip: 'Document ID' },
    ] as ColDef<CallNoteEntry | RowDateSeparator>[];
  });
  /**
   * Billing column defs — feature codes get hash-colored chips so repeated
   * codes carry a stable tone across the table. Sum column uses a sumCellRenderer
   * that bolds non-zero values (the cost-relevant rows) and mutes the zeros.
   * Date separator is reused via the generic full-width-row hooks.
   */
  /**
   * Transcript columns — Speaker chip palette (AGENT primary / CALLER ring-purple)
   * + wide wrapping text column. The sequence number and speaker tone make rapid
   * back-and-forth dialogues readable at scroll-speed.
   */
  readonly transcriptColumnDefs = computed<ColDef<TranscriptEntry>[]>(() => [
    { field: 'id',      headerName: '#',       width: 55,  maxWidth: 75,  cellClass: 'font-mono-feat text-muted-foreground' },
    { field: 'speaker', headerName: 'Speaker', width: 120, maxWidth: 150, cellRenderer: speakerCellRenderer },
    { field: 'text',    headerName: 'Text',    width: 600, flex: 1, tooltipField: 'text', wrapText: true, autoHeight: true, cellClass: 'leading-relaxed whitespace-pre-wrap' },
  ] as ColDef<TranscriptEntry>[]);

  readonly billingColumnDefs = computed<ColDef<BillingEntry | RowDateSeparator>[]>(() => [
    { field: 'billingTime',     headerName: 'Time',        width: 95,  maxWidth: 110, valueFormatter: timeOnlyFormatter, cellClass: 'font-mono-feat' },
    { field: 'feature',         headerName: 'Feature',     width: 130, maxWidth: 170, cellRenderer: featureCellRenderer, headerTooltip: 'Feature code — same code always shares the same chip color' },
    { field: 'featureShowName', headerName: 'Description', width: 240, flex: 1,       tooltipField: 'featureShowName' },
    { field: 'info',            headerName: 'Info',        width: 200, maxWidth: 280, tooltipField: 'info' },
    { field: 'amount',          headerName: 'Qty',         width: 60,  maxWidth: 80,  valueFormatter: amountFormatter, cellClass: 'font-mono-feat text-right', headerTooltip: 'Amount (units billed)' },
    { field: 'unitPrice',       headerName: 'Unit',        width: 95,  maxWidth: 115, valueFormatter: eurFormatter, cellClass: 'font-mono-feat text-right', headerTooltip: 'Unit price (EUR)' },
    { field: 'sum',             headerName: 'Sum',         width: 100, maxWidth: 125, cellRenderer: sumCellRenderer, cellClass: 'font-mono-feat text-right', headerTooltip: 'Line total (amount × unit price)' },
  ] as ColDef<BillingEntry | RowDateSeparator>[]);
  // transcriptCols moved to the `transcriptColumnDefs` computed signal above.

  /**
   * Distinct Call-IDs across the CDR rows + assigned label letter (A/B/C/…)
   * and hue index (0..5). Populated as a side-effect of fetchAccounting; the
   * legend strip above the table reads from it, as does the in-cell renderer
   * via callIdShortRenderer.
   */
  readonly callIdLegend = signal<ReadonlyArray<{ id: string; letter: string; hue: number }>>([]);

  /** O(1) lookup map used by the in-cell renderer to translate id -> {letter, hue}. */
  private readonly callIdLegendMap = computed(() => {
    const m = new Map<string, { letter: string; hue: number }>();
    for (const e of this.callIdLegend()) m.set(e.id, { letter: e.letter, hue: e.hue });
    return m;
  });

  /**
   * Resolve agent names for the distinct agent IDs in the CDR rows. Uses the
   * direct GET /phoneservice/v3/employees/<id> endpoint (single record,
   * includes firstName/lastName) — far cleaner than scraping the typeahead
   * search variant. Results are merged into agentNameMap, which the
   * cellRenderer reads.
   */
  private resolveAgentNames(rows: ReadonlyArray<CdrAccountingEntry>): void {
    const ids = new Set<string>();
    for (const r of rows) {
      // Backend sometimes returns `agent` as a number despite the string-only
      // model type; coerce with String() before trimming so this stays robust.
      const v = String(r.agent ?? '').trim();
      if (v) ids.add(v);
    }
    if (ids.size === 0) return;
    const existing = this.agentNameMap();
    const pending = Array.from(ids).filter((id) => !existing.has(id));
    if (pending.length === 0) return;
    // forkJoin batches all per-ID lookups, then a single atomic update +
    // single agentRefreshKey bump triggers ONE refreshCells pass on AG-Grid —
    // way better than N separate subscriptions each bumping the key.
    forkJoin(pending.map((id) => this.employees.findEmployeeById(id).pipe(map((e) => ({ id, employee: e })))))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        const next = new Map(this.agentNameMap());
        let added = false;
        for (const { id, employee } of results) {
          if (!employee) continue;
          const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ').trim();
          if (!name) continue;
          next.set(id, name);
          added = true;
        }
        if (!added) return;
        this.agentNameMap.set(next);
        this.agentRefreshKey.update((n) => n + 1);
      });
  }

  /**
   * Distinct subjects across the Call Notes rows + assigned letter + hue.
   * Populated as a side-effect of fetchCallNotes; the summary card reads from
   * it AND the table cellRenderer uses it for the letter chip. When the table
   * has only one distinct subject the Subject column is hidden (see
   * callNotesColumnDefs) and the single subject appears in the summary card.
   */
  readonly subjectLegend = signal<ReadonlyArray<{ subject: string; letter: string; hue: number }>>([]);
  private readonly subjectLegendMap = computed(() => {
    const m = new Map<string, { letter: string; hue: number }>();
    for (const e of this.subjectLegend()) m.set(e.subject, { letter: e.letter, hue: e.hue });
    return m;
  });

  /** Build the subject legend — distinct, non-empty, first-seen wins. */
  private buildSubjectLegend(rows: ReadonlyArray<CallNoteEntry>): void {
    const seen = new Set<string>();
    const out: { subject: string; letter: string; hue: number }[] = [];
    for (const r of rows) {
      const v = String(r.subject ?? '').trim();
      if (!v || seen.has(v)) continue;
      seen.add(v);
      out.push({ subject: v, letter: String.fromCharCode(65 + out.length), hue: hueIndex(v, 6) });
    }
    this.subjectLegend.set(out);
  }

  /**
   * In-cell Subject renderer — only used when the table shows >1 distinct
   * subject. Shows the letter chip with the matching hue; the full subject
   * text lives in the legend above the table.
   */
  readonly subjectShortRenderer = (params: { value: unknown }): string => {
    const v = String(params.value ?? '').trim();
    if (!v) return '';
    const entry = this.subjectLegendMap().get(v);
    if (!entry) return `<span class="cmt-callid-chip" title="${escapeHtml(v)}"><span class="font-mono-feat">?</span></span>`;
    return `<span class="cmt-callid-chip cmt-callid-hue-${entry.hue}" title="${escapeHtml(v)}"><span class="font-semibold">${entry.letter}</span></span>`;
  };

  /** Compute label letters + hues for distinct call IDs (first-seen wins). */
  private buildCallIdLegend(rows: ReadonlyArray<CdrAccountingEntry>): void {
    const seen = new Set<string>();
    const out: { id: string; letter: string; hue: number }[] = [];
    for (const r of rows) {
      for (const candidate of [r.callId1, r.callid2]) {
        const v = String(candidate ?? '').trim();
        if (!v || seen.has(v)) continue;
        seen.add(v);
        const letter = String.fromCharCode(65 + out.length); // A, B, C, …
        out.push({ id: v, letter, hue: hueIndex(v, 6) });
      }
    }
    this.callIdLegend.set(out);
  }

  // Fetchers — bound to the tab's input as a thunk that preserves `this`.
  // CDR rows are wrapped so date separators get injected between groups; the
  // tab passes the synthetic separator type along (widening to `any` is fine
  // because AG-Grid's column defs only touch the real entry fields).
  readonly fetchAccounting = (): Observable<(CdrAccountingEntry | RowDateSeparator)[]> =>
    this.service.findAccountingData(this.data.cchsid).pipe(
      tap((rows) => {
        this.buildCallIdLegend(rows ?? []);
        this.resolveAgentNames(rows ?? []);
      }),
      map((rows) => withDateSeparators(rows ?? [], (r) => r.timestamp)),
    );

  /**
   * Map of employee.id -> 'Firstname Lastname' built once from the cached employee
   * list. The Agent cellRenderer reads from here so each row shows a real name
   * next to the bare ID. Falls back to ID-only when no match.
   */
  readonly agentNameMap = signal<ReadonlyMap<string, string>>(new Map());

  /**
   * Cell-refresh trigger for the CDR table. Bumped after agentNameMap
   * resolves so AG-Grid re-runs the agent cellRenderer (which reads the
   * map). The tab's effect picks this up via the [cellRefreshKey] input.
   */
  readonly agentRefreshKey = signal(0);

  /**
   * Agent cellRenderer — 'Name · ID' when we can resolve the ID via the
   * employee cache, just 'ID' otherwise. Inline copy button covers the ID.
   */
  readonly agentCellRenderer = (params: { value: unknown }): string => {
    const raw = String(params.value ?? '').trim();
    if (!raw) return '';
    const id = escapeHtml(raw);
    const name = this.agentNameMap().get(raw);
    const nameHtml = name ? `<span class="text-foreground">${escapeHtml(name)}</span><span class="text-muted-foreground"> · </span>` : '';
    return (
      `<span class="inline-flex w-full items-center justify-between gap-1.5">` +
        `<span class="truncate">${nameHtml}<span class="font-mono-feat text-muted-foreground">${id}</span></span>` +
        `<button type="button" ` +
          `class="cmt-copy-cchsid inline-flex h-4 w-4 flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground cursor-pointer" ` +
          `data-cchsid="${id}" ` +
          `aria-label="Copy Agent ID" ` +
          `title="Copy ${id}">` +
          `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
            `<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>` +
            `<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>` +
          `</svg>` +
        `</button>` +
      `</span>`
    );
  };

  /**
   * In-cell Call-ID renderer — shows the assigned letter chip (A / B / C…) with
   * the hue from the legend above. Avoids repeating the full UUID per row.
   */
  readonly callIdShortRenderer = (params: { value: unknown }): string => {
    const v = String(params.value ?? '').trim();
    if (!v) return '';
    const entry = this.callIdLegendMap().get(v);
    if (!entry) {
      // No legend entry yet (race / unknown id) — fall back to a muted short form.
      const short = v.length > 8 ? v.slice(0, 6) + '…' : v;
      return `<span class="cmt-callid-chip" title="${v}"><span class="font-mono-feat">${short}</span></span>`;
    }
    return `<span class="cmt-callid-chip cmt-callid-hue-${entry.hue}" title="${v}"><span class="font-semibold">${entry.letter}</span></span>`;
  };

  /** AG-Grid full-width-row predicate for the CDR table — true for our synthetic separator rows. */
  readonly cdrIsFullWidthRow = (params: { rowNode: { data: unknown } }): boolean =>
    (params.rowNode.data as { __sep?: true } | null)?.__sep === true;

  /** Renderer for the CDR date-separator full-width row — light grey banner with the formatted date. */
  readonly cdrFullWidthCellRenderer = (params: { data: unknown }): string => {
    const iso = (params.data as { date?: string } | null)?.date;
    if (!iso) return '';
    return (
      `<div class="cmt-cdr-date-sep">` +
        `<span class="cmt-eyebrow">Date</span>` +
        // Sans (not mono) — the date label is a section heading, not an ID/amount.
        // text-base + font-semibold gives banner-style emphasis without serifs.
        `<span class="text-base font-semibold text-foreground">${formatDateBanner(iso)}</span>` +
      `</div>`
    );
  };
  readonly fetchCallNoteDetails = (): Observable<CallNoteDetails> =>
    this.service.findCallNoteDetails(this.data.lookupUserAccountId, this.data.callNoteId);
  readonly fetchCallNotes = (): Observable<(CallNoteEntry | RowDateSeparator)[]> =>
    this.data.callNoteId != null
      ? this.service.findCallNotes(this.data.callNoteId).pipe(
          // 404 = "no children documents" — expected for calls without follow-ups.
          // Treat as empty list rather than surfacing a red error.
          catchError((err) =>
            (err && (err.status ?? err.statusCode)) === 404 ? of<CallNoteEntry[]>([]) : (() => { throw err; })(),
          ),
          tap((rows) => this.buildSubjectLegend(rows ?? [])),
          map((rows) => withDateSeparators(rows ?? [], (r) => r.created)),
        )
      : of([]);
  readonly fetchBillings = (): Observable<(BillingEntry | RowDateSeparator)[]> =>
    this.data.callNoteId != null
      ? this.service.findBillings(this.data.callNoteId).pipe(
          // 404 = "no billing records for this call" — expected for free calls
          // or calls not yet billed. Render as empty list, not error.
          catchError((err) =>
            (err && (err.status ?? err.statusCode)) === 404 ? of<BillingEntry[]>([]) : (() => { throw err; })(),
          ),
          tap((rows) =>
            this.billingTotal.set((rows ?? []).reduce((acc, b) => acc + (Number(b.sum) || 0), 0)),
          ),
          map((rows) => withDateSeparators(rows ?? [], (r) => r.billingTime)),
        )
      : of([]);

  /**
   * Total of all billing.sum values across the rows. Populated by both the
   * eager constructor fetch (so the Overview / Billing summary card can show
   * it without waiting for the tab to render) AND by the fetchBillings pipe
   * (in case the rows refresh).
   */
  readonly billingTotal = signal<number | null>(null);

  /** Formatted total for the summary card; '—' when unknown. */
  readonly billingTotalLabel = computed<string>(() => {
    const v = this.billingTotal();
    if (v == null) return '—';
    return EUR_CURRENCY_FMT.format(v);
  });
  /**
   * Transcript chat state. Loaded once when the Transcript tab activates;
   * search filters the rendered list client-side. Separate from the AG-Grid
   * fetchFn flow because the chat layout doesn't need columnDefs.
   */
  readonly transcriptEntries = signal<TranscriptEntry[]>([]);
  readonly transcriptLoading = signal(false);
  readonly transcriptError = signal<{ status: number; message: string; correlationId?: string } | null>(null);
  /** Guard against the constructor `effect()` retriggering loadTranscript() on
   *  every error → infinite OPTIONS preflights. Set once we've kicked off a
   *  fetch for this dialog instance; only `loadTranscript()` resets it (so the
   *  Retry button still works). */
  private transcriptFetchTriggered = signal(false);
  readonly transcriptSearch = signal('');
  readonly transcriptFiltered = computed<TranscriptEntry[]>(() => {
    const q = this.transcriptSearch().trim().toLowerCase();
    if (!q) return this.transcriptEntries();
    return this.transcriptEntries().filter(
      (e) =>
        String(e.text ?? '').toLowerCase().includes(q) ||
        String(e.speaker ?? '').toLowerCase().includes(q),
    );
  });

  /** Bubble alignment per speaker — CALLER left, AGENT (incl. bots) right. */
  bubbleSideClass(speaker: string | null | undefined): string {
    return this.actorSide(speaker) === 'caller' ? 'justify-start' : 'justify-end';
  }

  /** Logical actor side: 'agent' for ebuero side (incl. bots), 'caller' for customer side. */
  actorSide(speaker: string | null | undefined): 'agent' | 'caller' {
    const s = String(speaker ?? '').toUpperCase();
    if (s === 'CALLER' || s === 'CUSTOMER' || s === 'USER') return 'caller';
    return 'agent';
  }

  /**
   * Lucide icon name for the speaker's avatar — distinguishes between human
   * agents, voice bots and chat bots so future agent types are visually
   * separable at a glance.
   */
  actorAvatarIcon(speaker: string | null | undefined): string {
    const s = String(speaker ?? '').toUpperCase();
    if (s === 'CALLER' || s === 'CUSTOMER' || s === 'USER') return 'lucideUser';
    if (s === 'VOICEBOT' || s === 'VOICE_BOT' || s === 'VOICE-BOT') return 'lucideMicVocal';
    if (s === 'CHATBOT' || s === 'CHAT_BOT' || s === 'CHAT-BOT' || s === 'BOT') return 'lucideMessageSquare';
    if (s === 'SYSTEM') return 'lucideBadge';
    // Default agent: human ebuero employee
    return 'lucideHeadset';
  }

  /**
   * Display name for the speaker — prefers the entry's own `speakerName`,
   * then real caller/agent names from call summary, then a humanised role
   * label. This is what shows next to / above the bubble.
   */
  actorDisplayName(entry: TranscriptEntry): string {
    if (entry.speakerName && entry.speakerName.trim().length > 0) return entry.speakerName.trim();
    const side = this.actorSide(entry.speaker);
    if (side === 'agent') {
      const fromSummary = this.data.summary?.agentName?.trim();
      if (fromSummary) return fromSummary;
      const s = String(entry.speaker ?? '').toUpperCase();
      if (s === 'VOICEBOT' || s === 'VOICE_BOT') return 'Voice Bot';
      if (s === 'CHATBOT' || s === 'CHAT_BOT' || s === 'BOT') return 'Chat Bot';
      if (s === 'SYSTEM') return 'System';
      return 'Agent';
    }
    // Caller side — TODO: surface name from CallNoteDetails once eagerly loaded
    const phone = this.data.summary?.callerNumber?.trim();
    if (phone && phone.length > 0) return phone;
    return 'Caller';
  }

  /** Format a transcript line timestamp to Berlin HH:mm:ss. */
  formatLineTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return CHAT_TIME_FMT.format(d);
  }

  /** Avatar circle tone — matches the bubble palette so avatar+bubble read as the same speaker. */
  avatarToneClass(speaker: string | null | undefined): string {
    const s = String(speaker ?? '').toUpperCase();
    if (s === 'CALLER' || s === 'CUSTOMER' || s === 'USER') {
      return 'bg-ring-purple/20 text-ring-purple ring-1 ring-ring-purple/30';
    }
    if (s === 'VOICEBOT' || s === 'VOICE_BOT' || s === 'CHATBOT' || s === 'CHAT_BOT' || s === 'BOT') {
      return 'bg-accent/30 text-accent-foreground ring-1 ring-accent/40';
    }
    if (s === 'SYSTEM') return 'bg-muted text-muted-foreground ring-1 ring-border';
    return 'bg-primary/20 text-primary ring-1 ring-primary/30';
  }

  /** Tone for the bubble background — matches the chip palette so chips + bubbles read as the same speaker. */
  bubbleToneClass(speaker: string | null | undefined): string {
    const s = String(speaker ?? '').toUpperCase();
    if (s === 'AGENT' || s === 'EMPLOYEE' || s === 'EBUERO') {
      return 'bg-primary/10 border-primary/20 text-foreground rounded-2xl rounded-br-md';
    }
    if (s === 'CALLER' || s === 'CUSTOMER' || s === 'USER') {
      return 'bg-ring-purple/10 border-ring-purple/20 text-foreground rounded-2xl rounded-bl-md';
    }
    return 'bg-muted/60 border-border text-muted-foreground rounded-2xl';
  }

  loadTranscript(): void {
    this.transcriptFetchTriggered.set(true);
    this.transcriptLoading.set(true);
    this.transcriptError.set(null);
    this.fetchTranscript().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rows) => {
        const list = rows ?? [];
        this.transcriptEntries.set(list);
        this.transcriptLoading.set(false);
        // Audit (EB-3798): the operator read the transcribed content of a
        // customer call. Only when content was actually shown — an empty
        // transcript exposes nothing to audit.
        if (list.length > 0) {
          this.loggingService.usage('transcript.view', this.callAuditFields());
        } else if (!this.isMockTranscript()) {
          this.loadExistingMissingReport();
        }
      },
      error: (err) => {
        this.transcriptEntries.set([]);
        this.transcriptError.set({
          status: (err?.status ?? 0) as number,
          message: (err?.message ?? 'Failed to load transcript') as string,
          correlationId: err?.headers?.get?.('correlation-id') ?? undefined,
        });
        this.transcriptLoading.set(false);
      },
    });
  }

  onTranscriptSearch(event: Event): void {
    this.transcriptSearch.set((event.target as HTMLInputElement).value);
  }

  /** Export the (filtered) transcript as a readable .txt file. */
  exportTranscriptText(): void {
    const rows = this.transcriptFiltered();
    if (rows.length === 0) return;
    const lines = rows.map((r) => `[#${r.id}] ${r.speaker}: ${r.text}`);
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${this.data.cchsid}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    // Audit (EB-3798): call-content left AMIE as a downloaded file — a
    // data-egress event, logged with the exported line count for scale.
    this.loggingService.usage('transcript.export', { ...this.callAuditFields(), lines: rows.length });
  }

  /**
   * Transcript fetcher with test-env fallback. Backend `TRANSCRIPT_NOT_READY`
   * 404 in non-live environments swaps to a mocked example transcript and
   * flips `isMockTranscript` so the UI can show an info banner. Live env
   * surfaces real errors normally.
   */
  readonly fetchTranscript = (): Observable<TranscriptEntry[]> => {
    this.isMockTranscript.set(false);
    return this.service.findTranscript(this.data.cchsid, this.data.summary?.timestamp).pipe(
      catchError((err) => {
        const status = (err && (err.status ?? err.statusCode)) as number | undefined;
        // Non-live envs: any failure (404, 5xx, CORS, network) swaps to mock
        // data so the UI is reviewable without a working transcription backend.
        // Live env: 404 → empty state (expected for many calls); other errors
        // surface to the user.
        if (environment.portalEnvKey !== 'live') {
          this.isMockTranscript.set(true);
          // Dynamic import: keeps the mock dialogue out of the live bundle.
          // esbuild emits it as a separate chunk loaded only when this code path runs.
          return from(
            import('./mock-transcript.dev').then((m) =>
              m.buildMockTranscript(this.data.summary?.timestamp),
            ),
          );
        }
        if (status === 404) {
          return of<TranscriptEntry[]>([]);
        }
        throw err;
      }),
    );
  };

  /**
   * True when the transcript table is showing mocked sample data because the
   * test environment doesn't carry real transcripts. The Transcript tab shows
   * an info banner above the table when this is set.
   */
  readonly isMockTranscript = signal(false);

  // ---- Transcript feedback (QAS-6936) -----------------------------------

  private readonly transcriptFeedbackService = inject(TranscriptFeedbackService);

  /** Direction for the feedback bar; null for unknown or missing types. */
  readonly callDirection = computed(() => callDirectionOf(this.data.summary?.type));

  /**
   * "Report missing transcript" state for the empty state. Per dialog instance:
   * each call opens a fresh dialog with its own MAT_DIALOG_DATA, so it cannot
   * leak from one call to the next.
   */
  readonly missingReportState = signal<'idle' | 'sending' | 'reported' | 'saved' | 'error'>('idle');

  /**
   * aida answers 201 with a new report, or 200 with the user's existing row
   * unchanged. That row may be a rating, so the state follows its `kind`.
   */
  reportMissingTranscript(): void {
    if (this.missingReportState() === 'sending') return;
    this.missingReportState.set('sending');
    this.transcriptFeedbackService
      .reportMissing(this.data.cchsid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fb) => {
          if (fb.kind === 'rating') {
            this.missingReportState.set('saved');
            return;
          }
          this.missingReportState.set('reported');
          // Call audit fields only.
          this.loggingService.usage(LOG_EVENTS.transcriptMissingReport, this.callAuditFields());
        },
        error: () => this.missingReportState.set('error'),
      });
  }

  /**
   * Reopen case: earlier feedback for this call shows as Reported (missing
   * transcript) or Saved (rating), not as a button. Best effort: an error keeps
   * the button.
   */
  private loadExistingMissingReport(): void {
    this.transcriptFeedbackService
      .findMyFeedback(this.data.cchsid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fb) => {
          if (!fb || this.missingReportState() !== 'idle') return;
          this.missingReportState.set(fb.kind === 'rating' ? 'saved' : 'reported');
        },
        error: () => undefined,
      });
  }

  // ---- Recording (EB-4196) ----------------------------------------------

  /**
   * AD-8. Gates the PANEL, not the tab — the tab stays visible so the feature is
   * discoverable and users know to ask for access, matching Explain Routing and
   * Open Graylog in this same dialog.
   *
   * UI affordance only: TAPStore is unauthenticated, so a URL bypasses this
   * entirely.
   *
   * @ticket EB-4196
   */
  readonly canReadRecording = computed<boolean>(() =>
    this.permissionService.canRead('callRecording'),
  );

  readonly recordingUrl = signal<string | null>(null);
  readonly recordingLoading = signal(false);
  /** Nothing to play — drives the AD-6 empty state, deliberately not an error. */
  readonly recordingUnavailable = signal(false);
  /**
   * URL known, but the browser has not said whether the file exists yet.
   *
   * An `<audio>` only probes once it is in the DOM, so AD-7's three states are
   * not exclusive: rendering the player immediately made a call with no
   * recording flash a play bar before the empty state. It is mounted but hidden
   * until `loadedmetadata`. `display: none` does not stop a media element
   * loading, unlike a lazy image.
   */
  readonly recordingProbing = signal(false);
  /** Render the player. Says nothing about whether the file was confirmed. */
  readonly recordingReady = signal(false);
  /**
   * `loadedmetadata` fired — the file provably exists. Separate from
   * `recordingReady`, which the stall timeout also sets without proof.
   *
   * @ticket EB-4196
   */
  readonly recordingConfirmed = signal(false);
  /**
   * The lookup failed, so we know nothing about the recording — as opposed to
   * `recordingUnavailable`, which claims there isn't one (AD-6).
   *
   * @ticket EB-4196
   */
  readonly recordingError = signal<{ status: number; message: string } | null>(null);
  /**
   * AD-5 — the activation effect must not read what `loadRecording()` writes, or
   * the not-found path re-fetches forever. Same guard as the Transcript tab;
   * `loadRecording()` sets it first so Retry can call it directly.
   */
  private readonly recordingFetchTriggered = signal(false);

  /**
   * AD-4 — once per dialog, on tab activation. Never batched into the call
   * listing, or the audit reads "user listed 40 recordings" (CLT's useless bar).
   *
   * @ticket EB-4196
   */
  loadRecording(): void {
    // The gate is on the fetch, not just the render: composing the URL without
    // the permission would put a working TAPStore address in the DOM and the
    // network log, and the store answers unauthenticated. Hiding a player above
    // a live URL is not a gate.
    if (!this.canReadRecording()) return;
    this.recordingFetchTriggered.set(true);
    this.recordingLoading.set(true);
    this.recordingUnavailable.set(false);
    this.recordingError.set(null);
    this.recordingProbing.set(false);
    this.recordingReady.set(false);
    this.recordingConfirmed.set(false);
    this.recordingUrl.set(null);
    this.clearProbeTimeout();
    this.service
      .findRecordingUrl(this.data.cchsid, this.data.summary?.timestamp)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (location) => {
          this.recordingUrl.set(location.url);
          this.recordingLoading.set(false);
          // Player mounts hidden and probes; the metadata/error handlers decide
          // what the user actually sees.
          this.recordingProbing.set(true);
          this.startProbeTimeout();
        },
        error: (err) => {
          this.recordingUrl.set(null);
          this.recordingLoading.set(false);
          // Only the service's own checks prove there is no recording (AD-6);
          // a transport failure proves nothing, so it gets an error alert.
          if (isRecordingNotLocatable(err)) {
            this.recordingUnavailable.set(true);
            return;
          }
          this.recordingError.set({
            status: (err?.status ?? 0) as number,
            message: CallDetailsDialogComponent.recordingErrorMessage(err?.status),
          });
        },
      });
  }

  /**
   * The only path that reveals the controls — see `recordingProbing`.
   *
   * @ticket EB-4196
   */
  onRecordingLoadedMetadata(): void {
    this.clearProbeTimeout();
    this.recordingProbing.set(false);
    this.recordingReady.set(true);
    this.recordingConfirmed.set(true);
    // Audit (EB-3798): the operator obtained a playable recording of a customer
    // call — a compliance-sensitive access. Logged at the moment the file is
    // confirmed available; NOT on tab-open (which proves nothing) and never
    // batched into the call listing (AD-4 audit granularity). Fires once per
    // successful load — a Retry after a transient error re-confirms the file and
    // re-emits, which is correct: that is a genuine second access to audit.
    this.loggingService.usage('recording.access', this.callAuditFields());
  }

  /**
   * Stable audit field set for the compliance-sensitive call-content events
   * (`recording.access`, `transcript.view`, `transcript.export`). `cchsid`
   * identifies the call; `customerId` scopes it. The acting operator (`_userId`)
   * and brand (`_brandId`) are attached by {@link LoggingService} itself.
   *
   * @ticket EB-3798
   */
  private callAuditFields(): LogFields {
    const fields: LogFields = { cchsid: this.data.cchsid };
    if (this.data.customerId != null) fields['customerId'] = this.data.customerId;
    return fields;
  }

  /**
   * Never echoes the backend body: a Spring error body carries SQL, class names
   * and upstream hostnames. `isRecordingNotLocatable` has already returned, so
   * everything reaching here is upstream — there is no safe case.
   *
   * @ticket EB-4196
   */
  private static recordingErrorMessage(status: number | undefined): string {
    // No status: the request never completed — DNS, VPN, CORS, abort.
    if (!status) return 'Could not reach the recording service.';
    if (status >= 500) return 'The recording service returned an error.';
    return 'The recording could not be loaded.';
  }

  /**
   * Only meaningful until the file is confirmed (AD-7): afterwards an `(error)`
   * means playback broke, not that the recording is missing.
   *
   * Guards on `recordingConfirmed`, not `recordingReady` — the stall timeout and
   * a re-entered tab reveal the player without proof, and guarding on the render
   * flag left both stuck showing a dead player.
   *
   * `MediaError.code` is the only evidence this layer carries, so only code 4
   * may claim "not recorded" (AD-6). Caveat: with no CORS on the store, code 4
   * still merges 404 / 500 / DNS — the evidence is "the browser could not load
   * it", not "5xx ruled out". `event` is required so a template that drops
   * `$event` fails the build instead of falling back to that claim.
   *
   * @ticket EB-4196
   */
  onRecordingLoadError(event: Event): void {
    if (this.recordingUrl() == null) return;
    if (this.recordingConfirmed()) return;
    const code = (event.target as HTMLAudioElement | null)?.error?.code;

    // Aborted: proves nothing either way, so no verdict.
    if (code === MEDIA_ERR_ABORTED) return;

    // The bytes arrived (a truncated .wav decodes to 3), so the file exists —
    // claiming "not recorded" here is the QAS-6080 false-404. A rejected token
    // will land here too once the store is authenticated.
    if (code === MEDIA_ERR_NETWORK || code === MEDIA_ERR_DECODE) {
      this.clearProbeTimeout();
      this.recordingLoading.set(false);
      this.recordingProbing.set(false);
      this.recordingReady.set(false);
      this.recordingError.set({
        status: 0,
        message: 'The recording could not be played.',
      });
      return;
    }

    // Code 4, or no MediaError at all: what a missing file reports.
    this.clearProbeTimeout();
    this.recordingUrl.set(null);
    this.recordingUnavailable.set(true);
    this.recordingError.set(null);
    this.recordingLoading.set(false);
    this.recordingProbing.set(false);
    this.recordingReady.set(false);
  }

  /**
   * A stall (VPN dropped, store not answering) fires neither event and would
   * spin forever. On expiry we reveal the player rather than claim "no
   * recording" — we don't know that, so letting the user try is honest.
   */
  private probeTimeout: ReturnType<typeof setTimeout> | null = null;
  private static readonly PROBE_TIMEOUT_MS = 10_000;

  private startProbeTimeout(): void {
    this.probeTimeout = setTimeout(() => {
      if (this.recordingProbing()) {
        this.recordingProbing.set(false);
        this.recordingReady.set(true);
      }
    }, CallDetailsDialogComponent.PROBE_TIMEOUT_MS);
  }

  private clearProbeTimeout(): void {
    if (this.probeTimeout != null) {
      clearTimeout(this.probeTimeout);
      this.probeTimeout = null;
    }
  }

  /**
   * Billed indicator — fetched eagerly when the dialog opens so the Overview
   * card can show a 'Billed' badge without waiting for the Billing tab to load.
   * null = unknown / not yet fetched, true = >=1 billing entry, false = none.
   * Derived from the same /invoice/v1/billings endpoint as the Billing tab.
   */
  readonly isBilled = signal<boolean | null>(null);

  constructor() {
    // Auto-load transcript when the Transcript tab activates — once per dialog
    // lifetime, then search filters client-side from the cached list.
    effect(() => {
      // Fire once per dialog instance when the user lands on the Transcript tab.
      // The `transcriptFetchTriggered` guard prevents re-firing on every error
      // (which would re-trigger an infinite OPTIONS preflight loop). Retry uses
      // the same `loadTranscript()` entry point, which re-sets the flag.
      if (this.activeTab() === 'transcript' && !this.transcriptFetchTriggered()) {
        this.loadTranscript();
      }
    });
    // EB-4196 (AD-4): same one-shot activation pattern for the Recording tab.
    // The guard is load-bearing — see `recordingFetchTriggered` (AD-5).
    effect(() => {
      if (
        this.activeTab() === 'recording' &&
        this.canReadRecording() &&
        !this.recordingFetchTriggered()
      ) {
        this.loadRecording();
      }
    });
    // The probe timer is the one async path here that RxJS does not cover, so it
    // needs the same cancellation `takeUntilDestroyed` gives the HTTP calls —
    // otherwise closing the dialog mid-probe still fires it 10s later and writes
    // to disposed signals.
    this.destroyRef.onDestroy(() => this.clearProbeTimeout());
    if (this.data.callNoteId != null) {
      this.service.findBillings(this.data.callNoteId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (entries) => {
          const list = entries ?? [];
          this.isBilled.set(list.length > 0);
          this.billingTotal.set(list.reduce((acc, b) => acc + (Number(b.sum) || 0), 0));
        },
        error: () => {
          this.isBilled.set(null);
          this.billingTotal.set(null);
        },
      });
    }
    // NOTE: agent name resolution happens per CDR-row-batch via
    // resolveAgentNames() in the fetchAccounting pipe. The bare
    // /employees list endpoint omits firstName/lastName, so per-ID
    // searchEmployees() queries are required.
  }

  /** Reason strings — set when prerequisite IDs are missing. */
  readonly callNotesUnavailable = computed<string | null>(() =>
    this.data.callNoteId == null ? 'This call has no associated callnote.' : null,
  );
  readonly billingUnavailable = computed<string | null>(() =>
    this.data.callNoteId == null ? 'This call has no associated callnote, so no billing records can be loaded.' : null,
  );
  readonly callDetailsUnavailable = computed<string | null>(() => {
    if (this.data.callNoteId == null) return 'This call has no associated callnote.';
    if (this.data.lookupUserAccountId == null) return 'This call is missing a user account reference — cannot resolve the phone service.';
    return null;
  });

  /**
   * Copy any short string (Call ID, etc.) to clipboard with a brief toast.
   * Used by the CDR Call-ID legend. Mirrors the call-note tab helper so
   * operators get the same UX everywhere.
   */
  copyValue(value: string | number | null | undefined, label: string): void {
    if (value == null || value === '') return;
    void navigator.clipboard.writeText(String(value)).then(
      () => this.toast.open(`${label} copied`, '', { duration: 1500 }),
      () => this.toast.open(`Could not copy ${label}`, '', { duration: 2000, tone: 'error' }),
    );
  }

  /** Format the call-row timestamp for the CDR summary strip. */
  formatBerlinTimestamp(iso: string | null | undefined): string {
    return formatBerlin(iso) || '—';
  }

  /** Format the talk duration from data.durations for the CDR summary strip. */
  formatTalkDuration(): string {
    const s = this.data.durations?.callDurationInSeconds;
    if (s == null) return '—';
    const total = Math.floor(s);
    const m = Math.floor(total / 60);
    const sec = total % 60;
    const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
    return `${pad2(m)}:${pad2(sec)}`;
  }

  close(): void {
    this.dialogRef.close();
  }
}
